import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { LoadingSpinner } from '../components/ui/LoadingSpinner';
import { Progress } from '../components/ui/Progress';
import { apiClient } from '../lib/api/client';

interface ConsolidatedAuditReport {
  report_id: string;
  session_id: string;
  overall_quality_score: number;
  overall_grade: string;
  performance_rating: string;
  call_effectiveness_score: number;
  report_data: {
    executive_summary: any;
    detailed_analysis: any;
    coaching_insights: any[];
  };
  created_at: string;
}

export const ConsolidatedAuditReportPage: React.FC = () => {
  const { sessionId } = useParams<{ sessionId: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<ConsolidatedAuditReport | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({});

  useEffect(() => {
    fetchAuditReport();
  }, [sessionId]);

  const fetchAuditReport = async () => {
    try {
      setLoading(true);
      const response = await apiClient.client.get(`/api/v1/audit-reports/consolidated/${sessionId}`);
      setData(response.data);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to fetch audit report');
    } finally {
      setLoading(false);
    }
  };

  const getGradeColor = (grade: string) => {
    const colors: Record<string, string> = {
      'A': 'text-green-600 bg-green-100',
      'B': 'text-blue-600 bg-blue-100',
      'C': 'text-yellow-600 bg-yellow-100',
      'D': 'text-orange-600 bg-orange-100',
      'F': 'text-red-600 bg-red-100'
    };
    return colors[grade] || 'text-gray-600 bg-gray-100';
  };

  const getRatingColor = (rating: string) => {
    if (rating.includes('EXCELLENT')) return 'text-green-600';
    if (rating.includes('GOOD')) return 'text-blue-600';
    if (rating.includes('ACCEPTABLE')) return 'text-yellow-600';
    if (rating.includes('NEEDS')) return 'text-orange-600';
    return 'text-red-600';
  };

  const toggleSection = (section: string) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <LoadingSpinner />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">{error || 'Failed to load report'}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">📊 Consolidated Call Audit Report</h1>
            <p className="text-sm text-gray-600 dark:text-gray-400">Report ID: {data.report_id}</p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => window.print()}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 dark:bg-gray-800 dark:text-gray-200"
            >
              🖨️ Print
            </button>
            <button
              onClick={() => navigate(-1)}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 dark:bg-gray-800 dark:text-gray-200"
            >
              ← Back
            </button>
          </div>
        </div>
      </div>

      {/* Executive Summary */}
      <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl border border-blue-200 p-8">
        <h2 className="text-2xl font-bold text-gray-900 mb-6">Executive Summary</h2>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-white/80 backdrop-blur rounded-lg p-6">
            <div className="text-sm font-medium text-gray-600 mb-2">Overall Quality Score</div>
            <div className="text-4xl font-bold text-gray-900 mb-3">{data.overall_quality_score}/100</div>
            <Progress value={data.overall_quality_score} max={100} className="h-2" />
          </div>

          <div className="bg-white/80 backdrop-blur rounded-lg p-6">
            <div className="text-sm font-medium text-gray-600 mb-2">Grade</div>
            <div className={`text-5xl font-bold ${getGradeColor(data.overall_grade).split(' ')[0]} mb-3`}>
              {data.overall_grade}
            </div>
            <div className="text-sm text-gray-600">Letter Grade</div>
          </div>

          <div className="bg-white/80 backdrop-blur rounded-lg p-6">
            <div className="text-sm font-medium text-gray-600 mb-2">Performance Rating</div>
            <div className={`text-xl font-semibold ${getRatingColor(data.performance_rating)}`}>
              {data.performance_rating.replace('_', ' ')}
            </div>
            <div className="mt-3">
              <div className="text-sm text-gray-600 mb-1">Call Effectiveness</div>
              <div className="text-2xl font-bold text-gray-900">{data.call_effectiveness_score}/100</div>
            </div>
          </div>
        </div>

        {/* Key Findings */}
        {data.report_data.executive_summary?.key_findings && (
          <div className="mt-6 bg-white/80 backdrop-blur rounded-lg p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Key Findings</h3>
            <ul className="space-y-2">
              {data.report_data.executive_summary.key_findings.map((finding: string, index: number) => (
                <li key={index} className="flex items-start gap-2">
                  <span className="text-blue-600 mt-1">•</span>
                  <span className="text-gray-700">{finding}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {/* Detailed Analysis Sections */}
      {data.report_data.detailed_analysis && (
        <div className="space-y-4">
          {Object.entries(data.report_data.detailed_analysis).map(([key, value]: [string, any]) => (
            <div key={key} className="bg-white rounded-lg border border-gray-200 overflow-hidden">
              <button
                onClick={() => toggleSection(key)}
                className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <span className="text-lg">{expandedSections[key] ? '▼' : '▶'}</span>
                  <h3 className="text-lg font-semibold text-gray-900 capitalize">
                    {key.replace(/_/g, ' ')}
                  </h3>
                </div>
                {value.score !== undefined && (
                  <div className="flex items-center gap-2">
                    <span className="text-xl font-bold text-gray-900">{value.score}/100</span>
                    <div className="w-24">
                      <Progress value={value.score} max={100} className="h-1.5" />
                    </div>
                  </div>
                )}
              </button>

              {expandedSections[key] && (
                <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
                  <div className="space-y-4">
                    {value.strengths && value.strengths.length > 0 && (
                      <div>
                        <h4 className="text-sm font-semibold text-green-700 mb-2">✓ Strengths</h4>
                        <ul className="space-y-1">
                          {value.strengths.map((strength: string, i: number) => (
                            <li key={i} className="text-sm text-gray-700">• {strength}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {value.areas_for_improvement && value.areas_for_improvement.length > 0 && (
                      <div>
                        <h4 className="text-sm font-semibold text-orange-700 mb-2">⚠ Areas for Improvement</h4>
                        <ul className="space-y-1">
                          {value.areas_for_improvement.map((area: string, i: number) => (
                            <li key={i} className="text-sm text-gray-700">• {area}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {value.feedback && (
                      <div className="text-sm text-gray-700 bg-white p-4 rounded">
                        {value.feedback}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Coaching Insights */}
      {data.report_data.coaching_insights && data.report_data.coaching_insights.length > 0 && (
        <div className="bg-purple-50 rounded-lg border border-purple-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">💡 Coaching Insights & Recommendations</h3>
          <div className="space-y-3">
            {data.report_data.coaching_insights.map((insight: any, index: number) => (
              <div key={index} className="bg-white rounded-lg p-4 border border-purple-100">
                <div className="font-medium text-gray-900 mb-1">{insight.title}</div>
                <div className="text-sm text-gray-700">{insight.recommendation}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Report Metadata */}
      <div className="bg-gray-50 rounded-lg border border-gray-200 p-4">
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <span className="text-gray-600">Report ID:</span>
            <span className="ml-2 font-mono text-gray-900">{data.report_id}</span>
          </div>
          <div>
            <span className="text-gray-600">Created:</span>
            <span className="ml-2 text-gray-900">
              {new Date(data.created_at).toLocaleString()}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

